import {
  AttributeData,
  AttributeValueData,
  EmploymentData,
  EntityAttributeData,
  PersonData,
} from '@ysura/common';
import { filter, has, isArray, isNil, map } from 'lodash';

import { AttributeType, Media } from '@/services/graphql/types';
import { DeepPartial } from '@/types/data';

export const getFileName = (media?: DeepPartial<Media>) => {
  const documentName = media?.latestVersion?.document?.name ?? '';

  if (documentName) {
    return documentName;
  }

  const mediaFileExtension = media?.mediaType
    ? `.${media?.mediaType?.toLowerCase()}`
    : '';

  return `${media?.name ?? ''}${mediaFileExtension}`;
};

type AttributeValueSimple =
  | (string | undefined)
  | number
  | boolean
  | (string | undefined)[];

export interface AttributeValueMinimal {
  name?: string;
  customerReference?: string;
  type?: string;
  values?: AttributeValueSimple;
}

const getAttributeValue = (
  type: string | undefined,
  values: AttributeValueData[]
): AttributeValueSimple => {
  if (type === AttributeType.MULTI_SELECTION) {
    return map(values, (value: AttributeValueData) => {
      return value.optionValue?.value;
    });
  } else if (type === AttributeType.SINGLE_SELECTION) {
    return values && values.length ? values[0].optionValue?.value : undefined;
  } else {
    const value = values[0];

    if (has(value, 'booleanValue')) {
      return value.booleanValue;
    } else if (has(value, 'dateValue')) {
      return value.dateValue;
    } else if (has(value, 'numericValue')) {
      return value.numericValue;
    } else if (has(value, 'textValue')) {
      return value.textValue;
    }
  }
};

const transformAttributes = (
  attributeItem: EntityAttributeData
): AttributeValueMinimal => {
  const attribute: AttributeData = attributeItem.attribute || {};
  const values: AttributeValueData[] = attributeItem.values || [];

  const simpleAttributeValues: AttributeValueSimple = getAttributeValue(
    attribute.type,
    values
  );

  return {
    name: attribute.name,
    customerReference: attribute.customerReference,
    type: attribute.type,
    values: simpleAttributeValues,
  };
};

export const transformAttendeeDataForMediaInjection = (
  attendees: Array<PersonData>
) => {
  const attendeesResult = map(attendees, (attendee: PersonData) => {
    //
    const attributes: AttributeValueMinimal[] = map(
      attendee.attributes,
      (a: EntityAttributeData) => {
        return transformAttributes(a);
      }
    );

    const filteredAttributes = filter(
      attributes,
      (it: AttributeValueMinimal) =>
        isArray(it.values) ? it.values.length : !isNil(it.values)
    );

    const employmentFunction = map(
      attendee.employments,
      (it: EmploymentData) => {
        return {
          name: it.employmentFunction?.name,
          customerReference: it.employmentFunction?.customerReference,
        };
      }
    );

    return { ...attendee, employmentFunction, attributes: filteredAttributes };
  });

  return attendeesResult;
};
